import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../config/firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // 'customer' or 'admin'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isCheckingRole, setIsCheckingRole] = useState(false);

  // Check if user is admin
  const checkUserRole = async () => {
    if (isCheckingRole) {
      console.log('Already checking role, skipping duplicate call');
      return;
    }
    
    setIsCheckingRole(true);
    try {
      const response = await api.getProfile();
      console.log('Profile API Response:', response);
      if (response.success && response.data) {
        console.log('Setting user role:', response.data.role, 'for user:', response.data.email);
        setUserRole(response.data.role);
        setIsCheckingRole(false);
        return response.data.role;
      }
    } catch (err) {
      // If token is invalid (401), sign out the user
      if (err.status === 401) {
        console.log('Token expired or invalid, signing out...');
        await signOut(auth);
        setUser(null);
        setUserRole(null);
      } else {
        console.error('Error checking user role:', err);
      }
      setUserRole(null);
    }
    setIsCheckingRole(false);
    return null;
  };

  // Sign up with email
  const signUpWithEmail = async (email, password, userData) => {
    try {
      setError(null);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Wait for token to be ready
      await userCredential.user.getIdToken();
      
      // User will be auto-created in MySQL via backend middleware on first API call
      setUser(userCredential.user);
      
      // Make API call to create user in MySQL and update profile
      try {
        await api.updateProfile({
          name: userData.name || '',
          phone: userData.phone || '',
          address: userData.address || '',
        });
        console.log('User profile created in MySQL');
      } catch (apiErr) {
        console.error('Error creating user in MySQL:', apiErr);
        // Don't throw - user is created in Firebase, they can update profile later
      }
      
      setUserRole('customer');
      return userCredential.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Sign in with email
  const signInWithEmail = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      
      // Force complete sign out
      try {
        await signOut(auth);
      } catch (e) {
        // Ignore signout errors
      }
      
      // Clear all state
      setUser(null);
      setUserRole(null);
      setIsCheckingRole(false);
      localStorage.clear();
      sessionStorage.clear();
      
      // Wait for Firebase to fully clear
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Sign in with new user
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('Login successful - Firebase user:', userCredential.user.email, 'UID:', userCredential.user.uid);
      
      // Immediately get and verify token
      const token = await userCredential.user.getIdToken(true);
      const tokenParts = token.split('.');
      const payload = JSON.parse(atob(tokenParts[1]));
      console.log('Fresh token UID:', payload.sub, 'Email:', payload.email);
      
      if (payload.sub !== userCredential.user.uid) {
        throw new Error('Token UID mismatch after login');
      }
      
      setUser(userCredential.user);
      setLoading(false);
      return userCredential.user;
    } catch (err) {
      setLoading(false);
      setError(err.message);
      throw err;
    }
  };

  // Sign out
  const logout = async () => {
    try {
      setError(null);
      await signOut(auth);
      setUser(null);
      setUserRole(null);
      setIsCheckingRole(false);
      
      // Clear all cached data
      localStorage.clear();
      sessionStorage.clear();
      
      // Force page reload to clear Firebase IndexedDB cache
      window.location.href = '/';
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Listen to auth state changes
  useEffect(() => {
    let isSubscribed = true;
    
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!isSubscribed) return;
      
      setLoading(true);
      if (currentUser) {
        setUser(currentUser);
        // Get fresh token and check if it's valid
        try {
          const token = await currentUser.getIdToken(true); // Force refresh
          if (token && isSubscribed) {
            // Add delay to ensure Firebase auth state is fully synced
            await new Promise(resolve => setTimeout(resolve, 300));
            if (isSubscribed) {
              await checkUserRole();
            }
          } else {
            // No valid token, sign out
            await signOut(auth);
            setUser(null);
            setUserRole(null);
          }
        } catch (err) {
          console.error('Error getting user token:', err);
          // Token refresh failed, sign out
          await signOut(auth);
          setUser(null);
          setUserRole(null);
        }
      } else {
        setUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return () => {
      isSubscribed = false;
      unsubscribe();
    };
  }, []);

  const value = {
    user,
    userRole,
    loading,
    error,
    signUpWithEmail,
    signInWithEmail,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
